import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PublicSurveyForm } from '../components/PublicSurveyForm';
import { FormStructure } from '../features/surveys/types';

const API_URL = import.meta.env.DEV ? '/api' : `${import.meta.env.VITE_API_URL || 'http://localhost:4800'}/api`;
const publicApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface SurveyData {
  survey: {
    id: string;
    title: string;
    description?: string;
    type: string;
  };
  form: {
    id: string;
    version: number;
    revision?: number;
    structure: FormStructure;
  };
}

export function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);

  useEffect(() => {
    const fetchSurvey = async () => {
      if (!token) {
        setError('Token no válido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Usar el endpoint público sin autenticación
        const { data } = await publicApi.get<{ success: boolean; data: SurveyData }>(
          `public/surveys/${token}`
        );
        setSurveyData(data.data);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Error al cargar la encuesta';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [token]);

  const handleSubmit = async (formData: Record<string, any>) => {
    if (!token) {
      throw new Error('Token no válido');
    }

    try {
      setSubmitting(true);
      await publicApi.post(`public/surveys/${token}/responses`, { data: formData });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Error al enviar la respuesta';
      throw new Error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando encuesta...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">
              El link puede haber expirado o ser inválido. Por favor, solicita un nuevo link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {surveyData.survey.title}
            </h1>
            {surveyData.survey.description && (
              <p className="text-muted-foreground">{surveyData.survey.description}</p>
            )}
          </div>

          <PublicSurveyForm
            structure={surveyData.form.structure}
            onSubmit={handleSubmit}
            loading={submitting}
          />
        </div>
      </div>
    </div>
  );
}

